import { useEffect, useMemo } from 'react';
import { Button, Form, Select } from 'antd';
import { UpOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import { useSearchParams } from '../../../../hooks/useSearchParams';
import { getValidSearchParams } from '../../../../utils';
import { CommentStatus, CommentStatusTitle } from '../../../../store/comment';
import { InputName } from '../../../Common/InputName';
import { RangePicker } from '../../../Common/RangePicker';
import { CommentEntity, CommentEntityTitle, CommentListParams } from '../../../../store/comment/list';
import SelectCategory from '../../../Common/SelectCategory';

import styles from './index.module.scss';

export const commentsListDefaultSearchParams: Partial<CommentListParams> = {
  page: 1,
  pageSize: 10,
  orderByColumn: 'createdAt',
  orderBy: 'DESC',
  text: '',
  category: '',
  subCategory: '',
  article: '',
  entity: null,
  status: null,
};

export interface FilterFormValues {
  text?: string;
  status?: CommentStatus | null;
  updatedAt?: [Date, Date];
  categoryAndSubCategoryIds: (string | null)[];
  entity: CommentEntity | null;
}

export const commentsListDefaultFormValues = {
  comment_text: '',
  comment_status: null,
  comment_updatedAt: undefined,
  comment_categoryAndSubCategoryIds: [],
  comment_entity: null,
};

interface QueryValues {
  text?: string;
  status?: CommentStatus | null;
  comment_updatedAtStart?: string;
  comment_updatedAtEnd?: string;
  comment_category?: string;
  comment_subCategory?: string;
}

interface FilterProps {
  onClose: () => void;
}

function Filter({ onClose }: FilterProps) {
  const [form] = Form.useForm<FilterFormValues>();

  const { searchParams, replaceSearchParams } = useSearchParams();

  const onFinish = ({ updatedAt, categoryAndSubCategoryIds, ...values }: FilterFormValues) => {
    const newValues: QueryValues = {
      comment_updatedAtStart: updatedAt?.[0].toISOString() || '',
      comment_updatedAtEnd: updatedAt?.[1].toISOString() || '',
      comment_category: categoryAndSubCategoryIds?.[0] || '',
      comment_subCategory: categoryAndSubCategoryIds?.[1] || '',
    };

    Object.keys(values).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      newValues[`comment_${key}`] = values[key] || '';
    });

    replaceSearchParams({
      comment_page: '1',
      ...newValues,
    } as Record<string, string>);
  };

  useEffect(() => {
    const {
      comment_updatedAtStart,
      comment_updatedAtEnd,
      comment_category,
      comment_subCategory,
      ...values
    } = getValidSearchParams<QueryValues>(
      (keys) => keys.filter((key) => key.indexOf('comment_') === 0),
      searchParams,
    );

    form.setFieldsValue({
      ...commentsListDefaultFormValues,
      ...values,
      updatedAt:
        comment_updatedAtStart && comment_updatedAtEnd
          ? [dayjs(comment_updatedAtStart), dayjs(comment_updatedAtEnd)]
          : commentsListDefaultFormValues.comment_updatedAt,
      categoryAndSubCategoryIds:
        comment_category && comment_subCategory
          ? [comment_category, comment_subCategory]
          : commentsListDefaultFormValues.comment_categoryAndSubCategoryIds,
    });
  }, [searchParams]);

  const clear = () => {
    replaceSearchParams(commentsListDefaultSearchParams, { replace: true });
  };

  const initialValues = useMemo(() => {
    const data = getValidSearchParams(
      (keys) => keys.filter((key) => key.indexOf('comment_') === 0),
      searchParams,
    );

    Object.keys(data).forEach((key) => {
      if (data[key]) {
        data[key.replace('comment_', '')] = data[key];
        delete data[key];
      }
    });

    return data;
  }, [searchParams]);

  return (
    <Form<FilterFormValues>
      className={styles.form}
      onFinish={onFinish}
      initialValues={initialValues}
      form={form}
    >
      <div className={styles.formRow}>
        <Form.Item label="User Type" name="role">
          <Select placeholder="User Type" allowClear>
            <Select.Option value="user">User</Select.Option>
            <Select.Option value="worker">Worker</Select.Option>
            <Select.Option value="business_owner">Business Owner</Select.Option>
          </Select>
        </Form.Item>

        <InputName formItemProps={{ label: 'Title', name: 'text' }} />
        <RangePicker form={form} />
        <SelectCategory />
        <Form.Item label="Status" name="status">
          <Select placeholder="Status" allowClear>
            {Object.entries(CommentStatusTitle).map(([value, label]) => (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item label="Entity" name="entity">
          <Select placeholder="Entity" allowClear>
            {Object.entries(CommentEntityTitle).map(([value, label]) => (
              <Select.Option key={value} value={value}>
                {label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </div>

      <div className={styles.buttons}>
        <Button htmlType="button" onClick={clear}>
          Clear
        </Button>
        <Button htmlType="submit" type="primary">
          Apply
        </Button>
        <Button htmlType="button" onClick={onClose}>
          Close
          {' '}
          <UpOutlined />
        </Button>
      </div>
    </Form>
  );
}

export default Filter;
